import React, {
  ChangeEvent,
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Button from 'components/Button';
import { AnswerFormProps } from 'views/Questions/components/AnswerForm/AnswerForm.types';
import { appAuthContext } from 'context/AuthContext';
import showNotification from 'utils/showNotification';
import AuthModalController from 'components/AuthModalController/AuthModalController';
import postNewAnswerResponse from '../../api/postNewAnswerResponse';
import styles from './AnswerForm.module.css';

const AnswerForm = ({ userWriteAnswer, questionId }: AnswerFormProps) => {
  const router = useRouter();
  const { user } = useContext(appAuthContext);
  const [showRegModal, setShowRegModal] = useState(false);
  const formRef = useRef(null);
  const textareaRef = useRef(null) as unknown as MutableRefObject<HTMLTextAreaElement>;
  const [answerEdit, setAnswerEdit] = useState(false);
  const [answerValue, setAnswerValue] = useState('');
  const [inputWasFocus, setInputWasFocus] = useState(false);
  const [textAreaOnFocus, setTextAreaOnFocus] = useState(false);

  useEffect(() => {
    if (userWriteAnswer) {
      setTimeout(() => {
        textareaOnFocus();
      }, 400);
    }
  }, [userWriteAnswer]);

  const [showLengthError, setShowLengthError] = useState(false);
  const answerLength = answerValue.length;
  const errorBorder = showLengthError && answerLength < 20 && !textAreaOnFocus;

  const textareaOnFocus = () => {
    setTextAreaOnFocus(true);
    setAnswerEdit(true);
  };

  const textAreaFocus = () => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const textAreaOnBlur = () => {
    setTextAreaOnFocus(false);
  };

  useEffect(() => {
    textAreaFocus();
  }, [answerEdit]);

  const changeTextAreaValue = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!inputWasFocus) setInputWasFocus(true);
    setAnswerValue(e.target.value);
  };

  const submitAnswerHandler = async () => {
    if (!answerEdit) {
      setAnswerEdit(true);
      return textAreaFocus();
    }
    if (answerLength < 20) {
      return setShowLengthError(true);
    }
    const postAnswerResponse = await postNewAnswerResponse(questionId, answerValue);
    if (postAnswerResponse && postAnswerResponse.success) {
      setAnswerValue('');
      setInputWasFocus(false);
      if (!user) {
        return setShowRegModal(true);
      }
      setShowLengthError(false);
      return await router.replace(router.asPath);
    }
    showNotification('error', 'Извини, произошла ошибка, попробуй позже');
  };

  return (
    <>
      <div
        ref={formRef}
        className={`${styles.answer} ${answerEdit ? styles.answer_edit : ''}`}
      >
        <form className={styles.answer__form}>
          <div className={styles.answer__avatar}>
            <Image
              alt=""
              src={
                user?.avatar ||
                `https://static.uchi.ru/files/students/avatars/avatar-5.png`
              }
              width={40}
              height={40}
            />
          </div>
          <div className={styles.answer__inputs}>
            {!answerEdit ? (
              <input
                value={answerValue}
                onChange={(e) => changeTextAreaValue(e)}
                onClick={textareaOnFocus}
                type="text"
                className={`${styles.answer__input} ${styles.answer__textarea} ${
                  showLengthError ? styles.length_error : ''
                }`}
                placeholder="Текст ответа..."
                maxLength={3500}
              />
            ) : (
              <textarea
                value={answerValue}
                onChange={(e) => changeTextAreaValue(e)}
                onBlur={textAreaOnBlur}
                ref={textareaRef}
                onFocus={textareaOnFocus}
                className={`${styles.answer__textarea} ${
                  errorBorder ? styles.length_error : ''
                }`}
                placeholder="Текст ответа..."
                name=""
                id="answer"
                rows={5}
                required
                maxLength={3500}
              />
            )}
            {showLengthError && (
              <span
                className={`${styles.length_error_text} ${
                  answerLength > 19 ? styles.length_error_text_gray : ''
                }`}
              >
                Ответ должен быть не менее 20 символов ({answerLength}/20)
              </span>
            )}
          </div>
        </form>
        <div className={styles.answer__btn_wrapper}>
          <Button
            className={`${styles.answer__send} ${
              errorBorder ? styles.length_error_btn : ''
            }`}
            text=""
            onClick={() => submitAnswerHandler()}
          >
            <>
              <span className={`${styles.answer__btn_text}`}>Ответить</span>
              <div className={`${styles.answer__mobile_icon}`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.5858 11H6C5.44771 11 5 11.4477 5 12C5 12.5523 5.44771 13 6 13H15.5858L12.2929 16.2929C11.9024 16.6834 11.9024 17.3166 12.2929 17.7071C12.6834 18.0976 13.3166 18.0976 13.7071 17.7071L18.7071 12.7071C19.0976 12.3166 19.0976 11.6834 18.7071 11.2929L13.7071 6.29289C13.3166 5.90237 12.6834 5.90237 12.2929 6.29289C11.9024 6.68342 11.9024 7.31658 12.2929 7.70711L15.5858 11Z"
                    fill="white"
                  />
                </svg>
              </div>
            </>
          </Button>
        </div>
      </div>
      {showRegModal && (
        <AuthModalController
          handleClose={() => {
            setShowRegModal(false);
          }}
          entryPoint={'answer'}
        />
      )}
    </>
  );
};

export default AnswerForm;
